import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Portfolio, Recommendation } from '../types';
import { RecommendationService } from './recommendationService';
import { PositionService } from './positionService';
import { PortfolioService } from './portfolioService';

/**
 * Check if US stock market is currently open
 * Market hours: 9:30 AM - 4:00 PM EST, Monday-Friday
 */
function isMarketOpen(): boolean {
  const now = new Date();

  // Convert to EST/EDT
  const estTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

  const hours = estTime.getHours();
  const minutes = estTime.getMinutes();
  const day = estTime.getDay(); // 0 = Sunday, 6 = Saturday

  // Weekend check
  if (day === 0 || day === 6) return false;

  // Market hours: 9:30 AM - 4:00 PM EST
  const timeInMinutes = hours * 60 + minutes;
  const marketOpen = 9 * 60 + 30;  // 9:30 AM = 570 minutes
  const marketClose = 16 * 60;      // 4:00 PM = 960 minutes

  return timeInMinutes >= marketOpen && timeInMinutes < marketClose;
}

export class AutopilotService {
  /**
   * Execute all pending recommendations for autopilot-enabled virtual portfolios
   */
  static async executeAutopilotRecommendations(): Promise<void> {
    console.log('=== AutopilotService: Starting execution ===');
    
    try {
      // Get all virtual portfolios with autopilot enabled
      const portfolios = await PortfolioService.getAllPortfolios();
      const autopilotPortfolios = portfolios.filter(
        p => p.type === 'virtual' && p.autopilotEnabled && p.isActive
      );
      
      console.log(`Found ${autopilotPortfolios.length} autopilot-enabled portfolios`);
      
      for (const portfolio of autopilotPortfolios) {
        console.log(`\nProcessing autopilot for portfolio: ${portfolio.name}`);
        await this.processPortfolioRecommendations(portfolio);
        
        // Update last run timestamp
        await this.updateAutopilotLastRun(portfolio.id);
      }
      
      console.log('\n=== AutopilotService: Execution completed ===');
    } catch (error) {
      console.error('Error in autopilot execution:', error);
      throw error;
    }
  }
  
  /**
   * Process recommendations for a single portfolio
   */
  private static async processPortfolioRecommendations(portfolio: Portfolio): Promise<void> {
    // Skip Alpaca-linked portfolios — backend handles their execution via Alpaca API
    if (portfolio.alpacaLinked) {
      console.log(`${portfolio.name} is Alpaca-linked — skipping frontend autopilot`);
      return;
    }

    // Check market hours FIRST - don't execute trades outside market hours
    if (!isMarketOpen()) {
      console.log(`Market closed - skipping autopilot execution for ${portfolio.name}`);
      return;
    }

    try {
      // Get active recommendations for this portfolio
      const recommendations = await RecommendationService.getPortfolioRecommendations(portfolio.id);
      const activeRecommendations = recommendations.filter(r => !r.executed && !r.dismissed);

      console.log(`${activeRecommendations.length} active recommendations found`);

      // Sort by priority: exits first, then adjustments, then entries
      const sortedRecs = this.sortRecommendationsByPriority(activeRecommendations);

      // Track running available funds and position count to prevent over-allocation
      // This is critical because we execute multiple recommendations in sequence
      // and need to track the cumulative effect on funds
      const openPositions = await PositionService.getOpenPositions(portfolio.id);
      let runningAvailableFunds = portfolio.availableFunds;
      let runningPositionCount = openPositions.length;

      console.log(`Starting with: $${runningAvailableFunds.toFixed(2)} available, ${runningPositionCount}/${portfolio.maxPositions} positions`);

      // Execute recommendations
      for (const recommendation of sortedRecs) {
        try {
          const result = await this.executeRecommendation(
            portfolio,
            recommendation,
            runningAvailableFunds,
            runningPositionCount
          );

          // Update running totals if a buy was executed
          if (result.executed && result.fundsUsed) {
            runningAvailableFunds -= result.fundsUsed;
            runningPositionCount++;
            console.log(`Updated running totals: $${runningAvailableFunds.toFixed(2)} available, ${runningPositionCount} positions`);
          }
        } catch (error) {
          console.error(`Failed to execute recommendation ${recommendation.id}:`, error);
          // Continue with other recommendations
        }
      }
    } catch (error) {
      console.error(`Error processing portfolio ${portfolio.id}:`, error);
      throw error;
    }
  }
  
  /**
   * Sort recommendations by execution priority
   */
  private static sortRecommendationsByPriority(recommendations: Recommendation[]): Recommendation[] {
    const typeOrder = { exit: 0, adjustment: 1, entry: 2, alert: 3 };
    const urgencyOrder = { immediate: 0, high: 1, medium: 2, low: 3 };
    
    return recommendations.sort((a, b) => {
      // First sort by type
      const typeDiff = typeOrder[a.type] - typeOrder[b.type];
      if (typeDiff !== 0) return typeDiff;
      
      // Then by urgency
      const urgencyDiff = urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
      if (urgencyDiff !== 0) return urgencyDiff;
      
      // Finally by score
      return b.score - a.score;
    });
  }
  
  /**
   * Execute a single recommendation
   * Returns execution result with funds used (for tracking running totals)
   */
  private static async executeRecommendation(
    portfolio: Portfolio,
    recommendation: Recommendation,
    runningAvailableFunds: number,
    runningPositionCount: number
  ): Promise<{ executed: boolean; fundsUsed?: number }> {
    console.log(`Executing ${recommendation.type} recommendation for ${recommendation.symbol}`);

    try {
      if (recommendation.type === 'entry' && recommendation.action === 'buy') {
        // Check if we've hit max positions (using running count)
        if (runningPositionCount >= portfolio.maxPositions) {
          console.log(`Portfolio at max capacity (${runningPositionCount}/${portfolio.maxPositions}), skipping buy`);
          return { executed: false };
        }

        const fundsUsed = await this.executeBuyRecommendation(
          portfolio,
          recommendation,
          runningAvailableFunds
        );

        if (fundsUsed > 0) {
          // Mark recommendation as executed
          await RecommendationService.markRecommendationExecuted(recommendation.id);
          console.log(`Successfully executed recommendation ${recommendation.id}`);
          return { executed: true, fundsUsed };
        }
        return { executed: false };
      } else if (recommendation.type === 'exit' && recommendation.action === 'sell') {
        await this.executeSellRecommendation(recommendation);
        await RecommendationService.markRecommendationExecuted(recommendation.id);
        console.log(`Successfully executed recommendation ${recommendation.id}`);
        return { executed: true };
      } else if (recommendation.type === 'adjustment') {
        await this.executeAdjustmentRecommendation(recommendation);
        await RecommendationService.markRecommendationExecuted(recommendation.id);
        console.log(`Successfully executed recommendation ${recommendation.id}`);
        return { executed: true };
      }

      return { executed: false };
    } catch (error) {
      console.error(`Error executing recommendation ${recommendation.id}:`, error);
      throw error;
    }
  }
  
  /**
   * Execute buy recommendation
   * Returns the funds used (0 if not executed)
   */
  private static async executeBuyRecommendation(
    portfolio: Portfolio,
    recommendation: Recommendation,
    runningAvailableFunds: number
  ): Promise<number> {
    const entryPrice = recommendation.recommendedEntryPrice || recommendation.currentPrice;
    let quantity = recommendation.recommendedQuantity || 0;

    // Calculate required funds
    const requiredFunds = quantity * entryPrice;

    // Use running available funds for the check (not stale portfolio.availableFunds)
    if (runningAvailableFunds < requiredFunds) {
      console.log(`Insufficient funds for buy order. Required: $${requiredFunds.toFixed(2)}, Available: $${runningAvailableFunds.toFixed(2)}`);

      // Try to adjust quantity based on running available funds
      const adjustedQuantity = Math.floor(runningAvailableFunds / entryPrice);

      if (adjustedQuantity < 10) { // Minimum viable position
        console.log('Adjusted quantity too small, skipping buy order');
        return 0;
      }

      quantity = adjustedQuantity;
      console.log(`Adjusted quantity to ${quantity} shares based on available funds`);
    }

    const fundsUsed = quantity * entryPrice;

    // Open position
    await PositionService.openPosition(
      portfolio.id,
      recommendation.symbol,
      quantity,
      entryPrice,
      recommendation.recommendedStopLoss || recommendation.currentPrice * 0.95,
      recommendation.recommendedTarget || recommendation.currentPrice * 1.10,
      recommendation.orderType === 'stop' ? 'limit' : recommendation.orderType || 'market',
      recommendation.orderType === 'limit' ? 'pending' : 'open',
      new Date()
    );

    console.log(`Opened ${quantity} shares of ${recommendation.symbol} at $${entryPrice.toFixed(2)} (used $${fundsUsed.toFixed(2)})`);
    return fundsUsed;
  }
  
  /**
   * Execute sell recommendation
   */
  private static async executeSellRecommendation(recommendation: Recommendation): Promise<void> {
    if (!recommendation.positionId) {
      throw new Error('No position ID for sell recommendation');
    }
    
    const position = await PositionService.getPosition(recommendation.positionId);
    if (!position) {
      throw new Error('Position not found');
    }
    
    await PositionService.closePosition(
      position.id,
      recommendation.currentPrice,
      new Date()
    );
    
    console.log(`Closed position ${position.symbol} at $${recommendation.currentPrice}`);
  }
  
  /**
   * Execute adjustment recommendation
   */
  private static async executeAdjustmentRecommendation(recommendation: Recommendation): Promise<void> {
    if (!recommendation.positionId || !recommendation.adjustmentValue) {
      throw new Error('Missing required data for adjustment');
    }
    
    if (recommendation.action === 'adjust_stop') {
      await PositionService.updateStopLoss(
        recommendation.positionId,
        recommendation.adjustmentValue
      );
      console.log(`Updated stop loss to $${recommendation.adjustmentValue}`);
    } else if (recommendation.action === 'adjust_target') {
      await PositionService.updateTargetPrice(
        recommendation.positionId,
        recommendation.adjustmentValue
      );
      console.log(`Updated target price to $${recommendation.adjustmentValue}`);
    }
  }
  
  /**
   * Update the last autopilot run timestamp
   */
  private static async updateAutopilotLastRun(portfolioId: string): Promise<void> {
    const portfolioRef = doc(db, 'portfolios', portfolioId);
    await updateDoc(portfolioRef, {
      autopilotLastRun: serverTimestamp()
    });
  }
  
  /**
   * Toggle autopilot for a portfolio
   */
  static async toggleAutopilot(portfolioId: string, enabled: boolean): Promise<void> {
    const portfolio = await PortfolioService.getPortfolio(portfolioId);
    
    if (!portfolio) {
      throw new Error('Portfolio not found');
    }
    
    if (portfolio.type !== 'virtual') {
      throw new Error('Autopilot can only be enabled for virtual portfolios');
    }
    
    const portfolioRef = doc(db, 'portfolios', portfolioId);
    await updateDoc(portfolioRef, {
      autopilotEnabled: enabled,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Autopilot ${enabled ? 'enabled' : 'disabled'} for portfolio ${portfolio.name}`);
    
    // If enabling, immediately run autopilot
    if (enabled) {
      // Process just this portfolio
      await this.processPortfolioRecommendations(portfolio);
      await this.updateAutopilotLastRun(portfolioId);
    }
  }
}